import React, { useContext } from "react";

import { Intro, Text } from "components/commons";
import useDataTable from "hooks/useDataTable";
import locale from "localization";
import DataTableV2 from "components/commons/data-table/data-table-v2";
import useExport from "hooks/useExport";
import { generateReportAdmin } from "apis";
import { getMerchantPayments, voidMerchantPayment } from "apis/non-fuel.api";
import { nonFuelFilterState } from "./non-fuel-filter.state";
import { mapDataToList, mapFilterToRequest } from "./non-fuel.mapper";
import { nonFuelColumns } from "./non-fuel-columns";
import NonFuelFilter from "./non-fuel-filter";
import { ConfirmModal, SuccessModal } from "components/modals";
import { SessionContext, UserContext } from "contexts";
import { useApi, useModal } from "hooks";
import { SettlementStatus, Portal, PlatformType, ErrorCode } from "enums";
import { formatAmount, handleRequest } from "utils";
import { useState } from "react";
import { useCallback } from "react";
import VoidPaymentModal from "./void-non-fuel-modal";
import ActionsHistoryModal from "./actions-history-modal";

const NonFuelModule = () => {
  const voidPaymentModal = useModal();
  const actionsHistoryModal = useModal();
  const confirmModal = useModal();

  const { sessionId } = useContext(SessionContext);
  const [remarks, setRemarks] = useState("");
  const [value, setValue] = useState("");

  const { user } = useContext(UserContext);
  const { role } = user?.userAccesses?.find((element) => {
    return element.portal === Portal.LOCQ;
  });

  const { request: voidRequest, loading: voidingRequest } = useApi({
    api: voidMerchantPayment,
    pageError: false,
    modalError: true,
    handleOwnError: {
      badrequest: true,
    },
  });

  const handleVoidPayment = ({
    merchantPaymentId,
    amount,
    rebate,
    settlementId,
    settlementStatus,
  }) => {
    setRemarks("");
    voidPaymentModal.show({
      title: `${locale.voidTransaction}?`,
      description: (
        <Text align="center">
          <locale.Populate
            text={
              settlementId
                ? settlementStatus === SettlementStatus.ForProcessing
                  ? locale.voidingThisTransactionSettlementProcessing
                  : locale.voidingThisTransactionSettlementProcessed
                : locale.voidingThisPayment
            }
            items={[<b>{formatAmount(amount)}</b>, <b>{formatAmount(rebate)}</b>]}
          />
        </Text>
      ),
      label: locale.reasonRemarks,
      actionText: locale.voidTransaction,
      submit: (value) => {
        onVoidPaymentCb({
          merchantPaymentId,
          value,
          amount,
        });
      },
    });
  };

  const handleActionsHistory = ({ voidedByEmail, status, voidedAt }) => {
    actionsHistoryModal.show({
      title: locale.actionsHistory,
      voidedByEmail,
      status,
      voidedAt,
    });
  };

  const onVoidPaymentCb = useCallback(
    async ({ merchantPaymentId, value, amount }) => {
      await handleRequest(
        async () => {
          await voidRequest({ merchantPaymentId, remarks: value });
          voidPaymentModal.close();
          table.fetch({});

          confirmModal.show({
            title: locale.transactionVoided,
            content: (
              <locale.Populate
                text={locale.thisPaymentHasBeenVoided}
                items={[<b>{formatAmount(amount)}</b>]}
              />
            ),
            loading: voidingRequest,
            primary: {
              text: locale.gotIt,
              onClick: () => {
                confirmModal.close();
              },
            },
          });
          setRemarks("");
        },
        {},
        (err) => {
          if (err?.data?.errorCode === ErrorCode.TxnVoidingWindowExpired) {
            voidPaymentModal.close();
            setRemarks("");
            setValue("");
            confirmModal.show({
              title: locale.unableToVoidTransaction,
              content: locale.voidingWindowExpiredMessage,
              primary: {
                text: locale.gotIt,
                onClick: () => {
                  confirmModal.close();
                },
              },
            });
            return;
          }

          return err.showError();
        }
      );
    },
    //eslint-disable-next-line
    [voidRequest, voidPaymentModal, confirmModal, voidingRequest]
  );

  const { filter, search, table } = useDataTable({
    api: { api: getMerchantPayments, params: { platformType: PlatformType.PLC } },
    filter: { initialState: nonFuelFilterState(), mapper: mapFilterToRequest },
    table: {
      key: "merchantPayments",
      mapper: (merchantPayments) =>
        mapDataToList({ merchantPayments, handleVoidPayment, handleActionsHistory, role }),
      columns: nonFuelColumns,
    },
  });

  const exports = useExport({
    api: generateReportAdmin,
    hasModal: true,
    reportType: "merchant-payment",
    mappedFilterState: {
      ...filter.mappedFilterState,
      merchantPaymentStatuses: filter?.mappedFilterState.status,
      searchKey: search.searchKey,
      sessionId,
    },
  });

  delete table.result.count;

  return (
    <>
      <SuccessModal {...exports?.successModalComponent} />
      <ConfirmModal {...confirmModal} />
      <div>
        <Intro
          title={locale.nonFuel}
          subtitle={locale.viewAndTrackScanToPayViaLOCQPayTransactions}
        />
      </div>
      <NonFuelFilter {...filter} {...search} {...exports} />
      <DataTableV2 {...table} />
      <ActionsHistoryModal {...actionsHistoryModal} />
      <VoidPaymentModal
        {...voidPaymentModal}
        remarks={remarks}
        onChangeRemarks={(v) => {
          setRemarks(v ? v : "");
        }}
        loading={voidingRequest}
        onSubmit={voidPaymentModal.submit}
        value={value}
        setValue={setValue}
      />
    </>
  );
};

export default NonFuelModule;
